import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Tooltip, CircularProgress, Alert, Dialog, DialogContent,
  DialogActions, LinearProgress, Chip, Collapse,
} from "@mui/material";
import SearchIcon             from "@mui/icons-material/Search";
import AddIcon                from "@mui/icons-material/Add";
import EditIcon               from "@mui/icons-material/Edit";
import DeleteIcon             from "@mui/icons-material/Delete";
import MeetingRoomIcon        from "@mui/icons-material/MeetingRoom";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";
import KeyboardArrowDownIcon  from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Inventory2Icon         from "@mui/icons-material/Inventory2";
import UnfoldMoreIcon         from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon         from "@mui/icons-material/UnfoldLess";
import VisibilityIcon         from "@mui/icons-material/Visibility";
import LaunchIcon             from "@mui/icons-material/Launch";
import api from "../../../services/api";

const C = {
  primary:   "#2563EB",
  primaryBg: "#EFF6FF",
  surface:   "#FFFFFF",
  surfaceAlt:"#F8FAFC",
  border:    "#E2E8F0",
  borderHov: "#CBD5E1",
  text1:     "#0F172A",
  text2:     "#475569",
  text3:     "#94A3B8",
  success:   "#16A34A",
  successBg: "#F0FDF4",
  successBdr:"#BBF7D0",
  warn:      "#D97706",
  warnBg:    "#FFFBEB",
  warnBdr:   "#FDE68A",
  danger:    "#DC2626",
  dangerBg:  "#FEF2F2",
  dangerBdr: "#FECACA",
  infoBdr:   "#BFDBFE",
  neutral:   "#64748B",
  neutralBg: "#F1F5F9",
  neutralBdr:"#CBD5E1",
};

const CONDITION_MAP = {
  baik:         { label: "Baik",         color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  cukup_baik:   { label: "Cukup Baik",   color: C.primary, bg: C.primaryBg, border: C.infoBdr,    dot: "#3B82F6" },
  rusak_ringan: { label: "Rusak Ringan",  color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  rusak_berat:  { label: "Rusak Berat",   color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

const STATUS_MAP = {
  aktif:           { label: "Aktif",           color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  tidak_aktif:     { label: "Tidak Aktif",     color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" },
  dipinjam:        { label: "Dipinjam",        color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  dalam_perbaikan: { label: "Dalam Perbaikan", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

function StatusPill({ map, value, label }) {
  const cfg = map[value] || { label: value || "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
  const displayLabel = label || cfg.label;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      px: "7px", py: "2px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px",
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {displayLabel}
      </Typography>
    </Box>
  );
}

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const thSx = {
  fontSize: "0.72rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`,
  py: "10px", px: "12px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};

const tdSx = {
  py: "11px", px: "12px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
};

function extractList(resData) {
  if (Array.isArray(resData))             return resData;
  if (Array.isArray(resData?.data))       return resData.data;
  if (Array.isArray(resData?.data?.data)) return resData.data.data;
  return [];
}

// Helper untuk mengelompokkan item di dalam suatu ruangan berdasarkan nama barang
function groupItems(items = []) {
  const groups = items.reduce((acc, item) => {
    const name = item.name || "Tanpa Nama";
    if (!acc[name]) {
      acc[name] = {
        name,
        unit: item.unit || "Unit",
        items: [],
        conditions: {},
        statuses: {},
      };
    }
    acc[name].items.push(item);
    const cond = item.condition || "baik";
    acc[name].conditions[cond] = (acc[name].conditions[cond] || 0) + 1;
    const stat = item.status || "aktif";
    acc[name].statuses[stat] = (acc[name].statuses[stat] || 0) + 1;
    return acc;
  }, {});

  return Object.values(groups).map((g) => {
    g.items.sort((a, b) => {
      const sa = a.serial_number || "";
      const sb = b.serial_number || "";
      return sa.length - sb.length || sa.localeCompare(sb);
    });
    return {
      ...g,
      total_quantity: g.items.length,
      first_serial: g.items[0]?.serial_number,
      last_serial: g.items[g.items.length - 1]?.serial_number,
    };
  });
}

const emptyForm = { name: "", code: "", description: "" };

export default function RoomsIndex() {
  const navigate = useNavigate();
  const rolePrefix = window.location.pathname.split("/")[1] || "superadmin";

  const [rooms,          setRooms]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [search,         setSearch]         = useState("");
  const [expandedRooms,  setExpandedRooms]  = useState({});
  const [expandedItems,  setExpandedItems]  = useState({});

  const [formOpen,   setFormOpen]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);
  const [formError,  setFormError]  = useState("");
  const [deleteId,   setDeleteId]   = useState(null);
  const [deleteRoom, setDeleteRoom] = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res  = await api.get("/rooms", { params });
      const list = extractList(res.data);
      // ── sort: terbanyak barang di atas ──
      list.sort((a, b) => (b.items_count ?? 0) - (a.items_count ?? 0));
      setRooms(list);
    } catch {
      setError("Gagal memuat data ruangan.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const toggleRoom = (roomId) => {
    setExpandedRooms((prev) => ({ ...prev, [roomId]: !prev[roomId] }));
  };

  const toggleExpandAllRooms = () => {
    const roomsWithItems = rooms.filter((r) => (r.items_count ?? 0) > 0);
    const allExpanded = roomsWithItems.length > 0 && roomsWithItems.every((r) => expandedRooms[r.id]);
    const nextState = {};
    rooms.forEach((r) => {
      nextState[r.id] = !allExpanded;
    });
    setExpandedRooms(nextState);
  };

  const toggleItemGroup = (roomId, groupName) => {
    const key = `${roomId}_${groupName}`;
    setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleExpandAllItemsInRoom = (roomId, groupedItems) => {
    const allExpanded = groupedItems.every((g) => expandedItems[`${roomId}_${g.name}`]);
    const nextState = { ...expandedItems };
    groupedItems.forEach((g) => {
      nextState[`${roomId}_${g.name}`] = !allExpanded;
    });
    setExpandedItems(nextState);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError("");
    setFormOpen(true);
  };

  const openEdit = (e, room) => {
    e.stopPropagation();
    setEditing(room);
    setForm({ name: room.name, code: room.code, description: room.description || "" });
    setFormError("");
    setFormOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError("");
    try {
      if (editing) await api.put(`/rooms/${editing.id}`, form);
      else         await api.post("/rooms", form);
      setFormOpen(false);
      fetchRooms();
    } catch (err) {
      const errs = err.response?.data?.errors;
      if (errs) setFormError(Object.values(errs).flat().join(" "));
      else      setFormError(err.response?.data?.message || "Gagal menyimpan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/rooms/${deleteId}`);
      setDeleteId(null);
      setDeleteRoom(null);
      fetchRooms();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal menghapus ruangan.");
      setDeleteId(null);
      setDeleteRoom(null);
    } finally {
      setDeleting(false);
    }
  };

  const totalItemsAcrossRooms = useMemo(() => {
    return rooms.reduce((sum, r) => sum + (r.items_count ?? 0), 0);
  }, [rooms]);

  const roomsWithItemsCount = useMemo(() => {
    return rooms.filter((r) => (r.items_count ?? 0) > 0).length;
  }, [rooms]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: "10px",
            backgroundColor: C.primaryBg, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <MeetingRoomIcon sx={{ fontSize: 20, color: C.primary }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
              Ruangan & Distribusi Barang
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
              Klik baris ruangan untuk melihat rincian barang yang dikelompokkan
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {roomsWithItemsCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={toggleExpandAllRooms}
              startIcon={rooms.filter(r => (r.items_count ?? 0) > 0).every(r => expandedRooms[r.id])
                ? <UnfoldLessIcon sx={{ fontSize: "16px !important" }} />
                : <UnfoldMoreIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                borderRadius: "8px", textTransform: "none",
                fontSize: "0.82rem", height: 34, px: 1.5,
                borderColor: C.border, color: C.text2, backgroundColor: C.surface,
                "&:hover": { backgroundColor: C.surfaceAlt, borderColor: C.borderHov },
              }}>
              {rooms.filter(r => (r.items_count ?? 0) > 0).every(r => expandedRooms[r.id])
                ? "Tutup Semua Ruangan"
                : "Buka Semua Ruangan"}
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
            onClick={openCreate}
            sx={{
              backgroundColor: C.primary, fontWeight: 600,
              borderRadius: "8px", textTransform: "none",
              fontSize: "0.82rem", height: 34, px: 2,
              boxShadow: "0 1px 3px rgba(37,99,235,0.35)",
              "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(37,99,235,0.4)" },
            }}>
            Tambah Ruangan
          </Button>
        </Box>
      </Box>

      {/* ── Alert ── */}
      {error && (
        <Alert severity="error" onClose={() => setError("")}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      {/* ── Search & Filter Bar ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", backgroundColor: C.surface, mb: 2 }}>
        <Box sx={{ p: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Cari nama ruangan, kode, atau nama barang..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: C.text3 }} />
                </InputAdornment>
              ),
              endAdornment: loading && search ? (
                <InputAdornment position="end">
                  <CircularProgress size={13} sx={{ color: C.primary }} />
                </InputAdornment>
              ) : null,
            }}
            sx={{ ...inputBase, width: { xs: "100%", sm: 360 } }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: "10px", py: "4px", backgroundColor: C.primaryBg,
              border: `1px solid ${C.infoBdr}`, borderRadius: "8px",
            }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: C.primary }}>
                {rooms.length} Ruangan
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: C.primary }} />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: C.text2 }}>
                {totalItemsAcrossRooms} Total Unit Barang
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{
          px: 2, py: "10px", display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt,
        }}>
          <Typography sx={{ fontSize: "0.78rem", color: C.text3, fontWeight: 500 }}>
            {loading ? "Memuat data..." : `Menampilkan ${rooms.length} ruangan`}
          </Typography>
          {loading && (
            <LinearProgress sx={{
              width: 80, height: 3, borderRadius: 4, backgroundColor: C.border,
              "& .MuiLinearProgress-bar": { backgroundColor: C.primary },
            }} />
          )}
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                <TableCell sx={{ ...thSx, width: 44, px: 1, textAlign: "center" }}></TableCell>
                <TableCell sx={thSx}>Nama Ruangan</TableCell>
                <TableCell sx={thSx}>Kode</TableCell>
                <TableCell sx={thSx}>Total Barang</TableCell>
                <TableCell sx={thSx}>Deskripsi</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>Aksi</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8, border: 0 }}>
                    <CircularProgress size={28} thickness={4} sx={{ color: C.primary }} />
                    <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: C.text3 }}>Memuat data...</Typography>
                  </TableCell>
                </TableRow>
              ) : rooms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8, border: 0 }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: "12px", backgroundColor: C.surfaceAlt,
                      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5,
                    }}>
                      <MeetingRoomIcon sx={{ fontSize: 24, color: C.text3 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.875rem", color: C.text2, fontWeight: 500 }}>
                      {search ? `Tidak ditemukan ruangan "${search}"` : "Belum ada data ruangan"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.5 }}>
                      {search ? "Coba kata kunci lain" : "Klik \"Tambah Ruangan\" untuk mulai"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rooms.map((room) => {
                  const isExpanded = Boolean(expandedRooms[room.id]);
                  const items = room.items || [];
                  const groupedItems = groupItems(items);
                  const hasItems = (room.items_count ?? 0) > 0 || items.length > 0;

                  return (
                    <Box component="tbody" key={room.id} sx={{ display: "contents" }}>
                      {/* Baris Utama Ruangan */}
                      <TableRow
                        onClick={() => toggleRoom(room.id)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isExpanded ? "#F1F5F9" : "transparent",
                          "&:hover": { backgroundColor: isExpanded ? "#E2E8F0" : C.primaryBg },
                          "&:hover .row-actions": { opacity: 1 },
                          borderBottom: isExpanded ? 0 : `1px solid ${C.border}`,
                          transition: "background 0.12s",
                        }}>

                        {/* Chevron Expand */}
                        <TableCell sx={{ ...tdSx, width: 44, px: 1, textAlign: "center" }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRoom(room.id);
                            }}
                            sx={{
                              width: 28, height: 28, borderRadius: "6px",
                              color: isExpanded ? C.primary : C.text3,
                              backgroundColor: isExpanded ? C.primaryBg : "transparent",
                              "&:hover": { backgroundColor: C.primaryBg, color: C.primary },
                              transition: "transform 0.18s",
                            }}>
                            {isExpanded ? (
                              <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </TableCell>

                        {/* Nama Ruangan */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/${rolePrefix}/rooms/${room.id}`);
                              }}
                              sx={{
                                fontSize: "0.875rem", fontWeight: 700, color: C.primary,
                                "&:hover": { textDecoration: "underline" },
                              }}>
                              {room.name}
                            </Typography>
                            <Tooltip title="Buka Halaman Detail Ruangan" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/${rolePrefix}/rooms/${room.id}`);
                                }}
                                sx={{ width: 22, height: 22, color: C.text3, "&:hover": { color: C.primary } }}>
                                <LaunchIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Kode */}
                        <TableCell sx={tdSx}>
                          <Box sx={{
                            display: "inline-flex", alignItems: "center",
                            px: "8px", py: "3px",
                            backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
                            borderRadius: "6px",
                          }}>
                            <Typography sx={{
                              fontSize: "0.72rem", fontWeight: 700, color: C.primary,
                              fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.04em",
                            }}>
                              {room.code}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Total Barang */}
                        <TableCell sx={tdSx}>
                          <Tooltip title={hasItems ? `${items.length || room.items_count} unit (${groupedItems.length} jenis barang) — Klik untuk melihat rincian` : "Tidak ada barang"} arrow>
                            <Box sx={{
                              display: "inline-flex", alignItems: "center", gap: 0.8,
                              px: "10px", py: "4px", borderRadius: "6px",
                              backgroundColor: hasItems ? C.successBg : C.surfaceAlt,
                              border: `1px solid ${hasItems ? C.successBdr : C.border}`,
                            }}>
                              <Typography sx={{
                                fontSize: "0.78rem", fontWeight: 700, fontVariantNumeric: "tabular-nums",
                                color: hasItems ? C.success : C.text3,
                              }}>
                                {room.items_count ?? items.length} Unit
                              </Typography>
                              {groupedItems.length > 0 && (
                                <Typography sx={{ fontSize: "0.7rem", color: C.text3 }}>
                                  ({groupedItems.length} jenis)
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        {/* Deskripsi */}
                        <TableCell sx={{ ...tdSx, maxWidth: 260 }}>
                          <Tooltip title={room.description || ""} arrow placement="top">
                            <Typography noWrap sx={{ fontSize: "0.78rem", color: room.description ? C.text2 : C.text3 }}>
                              {room.description || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Aksi */}
                        <TableCell sx={{ ...tdSx, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          <Box className="row-actions" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, opacity: { xs: 1, md: 0.7 }, transition: "opacity 0.15s" }}>
                            <Tooltip title="Lihat Detail Ruangan" arrow>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/${rolePrefix}/rooms/${room.id}`)}
                                sx={{
                                  width: 28, height: 28, borderRadius: "6px", color: C.text3,
                                  "&:hover": { color: C.primary, backgroundColor: C.primaryBg },
                                }}>
                                <VisibilityIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => openEdit(e, room)}
                                sx={{
                                  width: 28, height: 28, borderRadius: "6px", color: C.text3,
                                  "&:hover": { color: C.warn, backgroundColor: C.warnBg },
                                }}>
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Hapus" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(room.id);
                                  setDeleteRoom(room);
                                }}
                                sx={{
                                  width: 28, height: 28, borderRadius: "6px", color: C.text3,
                                  "&:hover": { color: C.danger, backgroundColor: C.dangerBg },
                                }}>
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Baris Accordion Rincian Barang di Ruangan Ini */}
                      <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${C.border}` : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{
                              p: { xs: 1.5, md: 2.5 },
                              pl: { xs: 2, md: 6 },
                              backgroundColor: "#F8FAFC",
                              borderTop: `1px solid ${C.border}`,
                            }}>
                              {/* Subheader Ruangan */}
                              <Box sx={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                flexWrap: "wrap", gap: 1.5, mb: 1.5,
                              }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Inventory2Icon sx={{ fontSize: 18, color: C.primary }} />
                                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: C.text1 }}>
                                    Daftar Barang di {room.name}
                                  </Typography>
                                  <Chip
                                    label={`${groupedItems.length} jenis barang · ${items.length} total unit`}
                                    size="small"
                                    sx={{
                                      fontSize: "0.72rem", fontWeight: 600, height: 22,
                                      backgroundColor: C.primaryBg, color: C.primary, border: `1px solid ${C.infoBdr}`,
                                    }}
                                  />
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {groupedItems.length > 0 && (
                                    <Button
                                      size="small"
                                      onClick={() => toggleExpandAllItemsInRoom(room.id, groupedItems)}
                                      startIcon={groupedItems.every((g) => expandedItems[`${room.id}_${g.name}`])
                                        ? <UnfoldLessIcon sx={{ fontSize: "14px !important" }} />
                                        : <UnfoldMoreIcon sx={{ fontSize: "14px !important" }} />}
                                      sx={{
                                        textTransform: "none", fontSize: "0.72rem", color: C.text2, fontWeight: 600,
                                        height: 28, px: 1.2, border: `1px solid ${C.border}`, borderRadius: "6px",
                                        backgroundColor: C.surface,
                                        "&:hover": { backgroundColor: C.surfaceAlt, borderColor: C.borderHov },
                                      }}>
                                      {groupedItems.every((g) => expandedItems[`${room.id}_${g.name}`])
                                        ? "Tutup Semua Serial"
                                        : "Buka Semua Serial"}
                                    </Button>
                                  )}

                                  <Button
                                    size="small"
                                    onClick={() => navigate(`/${rolePrefix}/rooms/${room.id}`)}
                                    endIcon={<LaunchIcon sx={{ fontSize: "13px !important" }} />}
                                    sx={{
                                      textTransform: "none", fontSize: "0.72rem", color: C.primary, fontWeight: 600,
                                      height: 28, px: 1.5, borderRadius: "6px",
                                      backgroundColor: C.surface, border: `1px solid ${C.infoBdr}`,
                                      "&:hover": { backgroundColor: C.primaryBg },
                                    }}>
                                    Buka Halaman Ruangan
                                  </Button>
                                </Box>
                              </Box>

                              {/* Daftar Barang Grouped / Kosong */}
                              {groupedItems.length === 0 ? (
                                <Box sx={{
                                  p: 3, textAlign: "center", borderRadius: "8px",
                                  backgroundColor: C.surface, border: `1px dashed ${C.border}`,
                                }}>
                                  <Typography sx={{ fontSize: "0.82rem", color: C.text3 }}>
                                    Belum ada barang yang tercatat di ruangan ini.
                                  </Typography>
                                </Box>
                              ) : (
                                <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", backgroundColor: C.surface }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                                        <TableCell sx={{ ...thSx, width: 36, px: 1 }}></TableCell>
                                        <TableCell sx={thSx}>Nama Barang</TableCell>
                                        <TableCell sx={{ ...thSx, width: 110 }}>Total Unit</TableCell>
                                        <TableCell sx={thSx}>Rentang Serial Number</TableCell>
                                        <TableCell sx={thSx}>Kondisi</TableCell>
                                        <TableCell sx={thSx}>Status</TableCell>
                                        <TableCell sx={{ ...thSx, textAlign: "right", pr: 2 }}>Serial Number</TableCell>
                                      </TableRow>
                                    </TableHead>

                                    <TableBody>
                                      {groupedItems.map((group) => {
                                        const groupKey = `${room.id}_${group.name}`;
                                        const isGroupExpanded = Boolean(expandedItems[groupKey]);

                                        return (
                                          <Box component="tbody" key={group.name} sx={{ display: "contents" }}>
                                            {/* Baris Kelompok Barang */}
                                            <TableRow
                                              onClick={() => toggleItemGroup(room.id, group.name)}
                                              sx={{
                                                cursor: "pointer",
                                                backgroundColor: isGroupExpanded ? "#F8FAFC" : "transparent",
                                                "&:hover": { backgroundColor: isGroupExpanded ? "#F1F5F9" : "#F8FAFC" },
                                                borderBottom: `1px solid ${C.border}`,
                                                transition: "background 0.12s",
                                              }}>

                                              {/* Toggle Chevron */}
                                              <TableCell sx={{ ...tdSx, width: 36, px: 1, textAlign: "center" }}>
                                                <IconButton
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleItemGroup(room.id, group.name);
                                                  }}
                                                  sx={{
                                                    width: 24, height: 24, borderRadius: "4px",
                                                    color: isGroupExpanded ? C.primary : C.text3,
                                                    backgroundColor: isGroupExpanded ? C.primaryBg : "transparent",
                                                    "&:hover": { backgroundColor: C.primaryBg, color: C.primary },
                                                  }}>
                                                  {isGroupExpanded ? (
                                                    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                                  ) : (
                                                    <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                                                  )}
                                                </IconButton>
                                              </TableCell>

                                              {/* Nama Barang */}
                                              <TableCell sx={tdSx}>
                                                <Typography sx={{
                                                  fontSize: "0.82rem", fontWeight: 700, color: C.text1,
                                                  "&:hover": { color: C.primary },
                                                }}>
                                                  {group.name}
                                                </Typography>
                                              </TableCell>

                                              {/* Total Unit */}
                                              <TableCell sx={tdSx}>
                                                <Chip
                                                  label={`${group.total_quantity} ${group.unit}`}
                                                  size="small"
                                                  sx={{
                                                    fontSize: "0.72rem", fontWeight: 700, height: 22,
                                                    backgroundColor: C.primaryBg, color: C.primary,
                                                    border: `1px solid ${C.infoBdr}`,
                                                  }}
                                                />
                                              </TableCell>

                                              {/* Rentang Serial Number */}
                                              <TableCell sx={tdSx}>
                                                <Box sx={{
                                                  display: "inline-flex", alignItems: "center", gap: 0.5,
                                                  px: "7px", py: "2px", backgroundColor: C.surfaceAlt,
                                                  border: `1px solid ${C.border}`, borderRadius: "5px",
                                                  fontFamily: "'JetBrains Mono', monospace",
                                                  fontSize: "0.7rem", fontWeight: 600, color: C.text2,
                                                }}>
                                                  {group.first_serial === group.last_serial ? (
                                                    <span>{group.first_serial}</span>
                                                  ) : (
                                                    <span>{group.first_serial} &nbsp;—&nbsp; {group.last_serial}</span>
                                                  )}
                                                </Box>
                                              </TableCell>

                                              {/* Kondisi Breakdown */}
                                              <TableCell sx={tdSx}>
                                                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                                  {Object.entries(group.conditions).map(([cond, count]) => (
                                                    <StatusPill
                                                      key={cond}
                                                      map={CONDITION_MAP}
                                                      value={cond}
                                                      label={`${count} ${CONDITION_MAP[cond]?.label || cond}`}
                                                    />
                                                  ))}
                                                </Box>
                                              </TableCell>

                                              {/* Status Breakdown */}
                                              <TableCell sx={tdSx}>
                                                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                                  {Object.entries(group.statuses).map(([stat, count]) => (
                                                    <StatusPill
                                                      key={stat}
                                                      map={STATUS_MAP}
                                                      value={stat}
                                                      label={`${count} ${STATUS_MAP[stat]?.label || stat}`}
                                                    />
                                                  ))}
                                                </Box>
                                              </TableCell>

                                              {/* Tombol Klik Serial Number */}
                                              <TableCell sx={{ ...tdSx, textAlign: "right", pr: 2 }}>
                                                <Button
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleItemGroup(room.id, group.name);
                                                  }}
                                                  endIcon={isGroupExpanded ? <KeyboardArrowDownIcon sx={{ fontSize: "14px !important" }} /> : <KeyboardArrowRightIcon sx={{ fontSize: "14px !important" }} />}
                                                  sx={{
                                                    textTransform: "none", fontSize: "0.7rem", fontWeight: 600,
                                                    height: 24, px: 1, borderRadius: "5px",
                                                    backgroundColor: isGroupExpanded ? C.primaryBg : C.surfaceAlt,
                                                    color: isGroupExpanded ? C.primary : C.text2,
                                                    border: `1px solid ${isGroupExpanded ? C.infoBdr : C.border}`,
                                                    "&:hover": { backgroundColor: C.primaryBg, color: C.primary },
                                                  }}>
                                                  {isGroupExpanded ? "Tutup Serial" : `Lihat ${group.total_quantity} Serial`}
                                                </Button>
                                              </TableCell>
                                            </TableRow>

                                            {/* Baris Rincian Serial Number (Accordion Child) */}
                                            <TableRow sx={{ backgroundColor: "#F1F5F9" }}>
                                              <TableCell colSpan={7} sx={{ p: 0, borderBottom: isGroupExpanded ? `1px solid ${C.border}` : 0 }}>
                                                <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                                                  <Box sx={{
                                                    p: 2, pl: 5, pr: 3,
                                                    backgroundColor: "#F8FAFC",
                                                    borderBottom: `1px dashed ${C.border}`,
                                                  }}>
                                                    <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600, mb: 1 }}>
                                                      Daftar {group.items.length} Serial Number untuk &quot;{group.name}&quot; (Klik untuk buka detail barang):
                                                    </Typography>

                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                                                      {group.items.map((item, itemIdx) => {
                                                        const condCfg = CONDITION_MAP[item.condition] || { color: C.neutral, dot: "#94A3B8" };
                                                        return (
                                                          <Tooltip
                                                            key={item.id || itemIdx}
                                                            title={`Kondisi: ${CONDITION_MAP[item.condition]?.label || item.condition} • Status: ${STATUS_MAP[item.status]?.label || item.status} (Klik untuk lihat detail)`}
                                                            arrow>
                                                            <Box
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/${rolePrefix}/items/${item.id}`);
                                                              }}
                                                              sx={{
                                                                display: "inline-flex", alignItems: "center", gap: 0.8,
                                                                px: "8px", py: "4px",
                                                                backgroundColor: C.surface,
                                                                border: `1px solid ${C.border}`,
                                                                borderRadius: "6px",
                                                                cursor: "pointer",
                                                                transition: "all 0.15s",
                                                                "&:hover": {
                                                                  borderColor: C.primary,
                                                                  backgroundColor: C.primaryBg,
                                                                  transform: "translateY(-1px)",
                                                                  boxShadow: "0 2px 6px rgba(37,99,235,0.12)",
                                                                },
                                                              }}>
                                                              <Box sx={{
                                                                width: 6, height: 6, borderRadius: "50%",
                                                                backgroundColor: condCfg.dot, flexShrink: 0,
                                                              }} />
                                                              <Typography sx={{
                                                                fontSize: "0.72rem", fontWeight: 700,
                                                                fontFamily: "'JetBrains Mono', monospace",
                                                                color: C.text1,
                                                              }}>
                                                                {item.serial_number}
                                                              </Typography>
                                                              <LaunchIcon sx={{ fontSize: 11, color: C.text3 }} />
                                                            </Box>
                                                          </Tooltip>
                                                        );
                                                      })}
                                                    </Box>
                                                  </Box>
                                                </Collapse>
                                              </TableCell>
                                            </TableRow>
                                          </Box>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Paper>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Box>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Form Dialog ── */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "20px 24px 0", display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "9px", backgroundColor: C.primaryBg,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MeetingRoomIcon sx={{ fontSize: 18, color: C.primary }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1 }}>
            {editing ? "Edit Ruangan" : "Tambah Ruangan"}
          </Typography>
        </Box>

        <DialogContent sx={{ pt: "16px !important", px: "24px" }}>
          {formError && (
            <Alert severity="error" icon={<WarningAmberIcon fontSize="small" />}
              sx={{ mb: 2, borderRadius: "8px", fontSize: "0.8rem", border: `1px solid ${C.dangerBdr}` }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField fullWidth required label="Nama Ruangan"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              size="small" sx={inputBase} />
            <TextField fullWidth required label="Kode Ruangan"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
              size="small" sx={inputBase} placeholder="e.g. LAB-K01"
              helperText="Kode unik untuk identifikasi ruangan"
              FormHelperTextProps={{ sx: { fontSize: "0.72rem", color: C.text3, mt: 0.5 } }}
            />
            <TextField fullWidth label="Deskripsi (opsional)"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              size="small" multiline rows={2} sx={inputBase} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setFormOpen(false)}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleSave} disabled={saving || !form.name || !form.code}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 3,
              backgroundColor: C.primary, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "none" },
              "&:disabled": { backgroundColor: C.neutralBg, color: C.text3 } }}>
            {saving
              ? <CircularProgress size={15} sx={{ color: "#fff" }} />
              : (editing ? "Simpan Perubahan" : "Tambah Ruangan")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={Boolean(deleteId)} onClose={() => { setDeleteId(null); setDeleteRoom(null); }}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DeleteIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Hapus Ruangan?
              </Typography>
              {deleteRoom && (
                <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                  <strong>{deleteRoom.name}</strong>{" "}
                  <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.75rem",
                    backgroundColor: C.primaryBg, color: C.primary, px: "5px", py: "1px", borderRadius: "4px" }}>
                    {deleteRoom.code}
                  </Box>
                  {" "}akan dihapus permanen.
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px", backgroundColor: C.warnBg, border: `1px solid ${C.warnBdr}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.warn, display: "flex", alignItems: "center", gap: 0.75 }}>
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              Ruangan hanya bisa dihapus jika tidak memiliki barang di dalamnya.
            </Typography>
          </Box>
        </Box>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => { setDeleteId(null); setDeleteRoom(null); }}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleDelete} disabled={deleting} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600,
              boxShadow: "none", "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            {deleting ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Hapus"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
